"use client";
import {JSX, useState} from "react";

import Link from "next/link";

import SmartImage from "@/shared/ui/smart-image";

import {useKeenSlider} from "keen-slider/react";

import {useTranslation} from "@/shared/i18n";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {assetPath} from "@/shared/utils/asset-path";

import "@/sections/blog/post-hero/styles/post-hero.scss";
import "keen-slider/keen-slider.min.css";

type BlogPostHeroImage = {
    src: string;
    alt?: string;
};

export type BlogPostHeroProps = {
    title: string;
    category?: string;
    publishedAt?: string;
    images?: ReadonlyArray<BlogPostHeroImage>;
    showBackButton?: boolean;
    backButtonUrl?: string;
    container?: "default" | "full";
};

export function PostHero({
    title,
    category,
    images = [],
    showBackButton = true,
    backButtonUrl = "/blog",
    container = "default",
}: BlogPostHeroProps): JSX.Element {
    const {t} = useTranslation("common");

    const [first, ...rest] = images;
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
        loop: true,
        slides: {perView: 1},
        rubberband: true,
        slideChanged(s) {
            setCurrentSlide(s.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
    });
    const prev = (): void => slider.current?.prev();
    const next = (): void => slider.current?.next();

    const isSingle = rest.length === 0;

    return (
        <section className={`post-hero${container === "full" ? " post-hero--full" : ""}`}>
            <ContainerBase>
                {showBackButton && (
                    <div className="post-hero__header">
                        <Link href={backButtonUrl} className="post-hero__back">
                            ← {t("back")}
                        </Link>
                    </div>
                )}
                <div className="post-hero__content">
                    <div className="post-hero__title-row">
                        <Typography as="h1" size={{desktop: 20, mobile: 20}} weight={500} className="post-hero__title">
                            {title}
                        </Typography>
                        <div className="post-hero__meta">
                            {category && (
                                <Typography as="div" size={{desktop: 12, mobile: 10}} className="post-hero__category">
                                    {category}
                                </Typography>
                            )}
                        </div>
                    </div>

                    <div className={["post-hero__gallery", isSingle ? "post-hero__gallery--single" : ""].join(" ")}>
                        {first && (
                            <div className={["post-hero__img", isSingle ? "post-hero__img--landscape" : "post-hero__img--portrait"].join(" ")}>
                                <SmartImage
                                    src={first.src}
                                    alt={first.alt ?? ""}
                                    fill
                                    priority={true}
                                    sizes="(max-width: 1024px) 100vw, 850px"
                                    className="post-hero__image"
                                />
                            </div>
                        )}
                        {!isSingle && (
                            <div className="post-hero__img post-hero__img--landscape">
                                <div ref={sliderRef} className="keen-slider post-hero__slider">
                                    {rest?.map((img, index) => (
                                        <div className="keen-slider__slide post-hero__slide" key={`${img.src}-${index}`}>
                                            <div className="post-hero__slide-inner">
                                                <SmartImage
                                                    src={img.src}
                                                    alt={img.alt ?? ""}
                                                    fill
                                                    sizes="(max-width: 1024px) 100vw, 880px"
                                                    className="post-hero__image"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {rest.length > 0 && (
                                    <>
                                        <button type="button" aria-label="Previous" className="post-hero__nav post-hero__nav--prev" onClick={prev}>
                                            <SmartImage
                                                src={assetPath("/images/things-to-do/icons/arrow-right.svg")}
                                                alt="Prev"
                                                width={20}
                                                height={20}
                                            />
                                        </button>
                                        <button type="button" aria-label="Next" className="post-hero__nav post-hero__nav--next" onClick={next}>
                                            <SmartImage
                                                src={assetPath("/images/things-to-do/icons/arrow-right.svg")}
                                                alt="Next"
                                                width={20}
                                                height={20}
                                            />
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    {!isSingle && loaded && slider.current && (slider.current.track.details?.slides?.length ?? 0) > 0 && (
                        <div className="post-hero__dots">
                            {Array.from({length: slider.current.track.details.slides.length}).map((_, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    aria-label={`Go to slide ${idx + 1}`}
                                    className={`post-hero__dot${currentSlide === idx ? " is-active" : ""}`}
                                    onClick={() => slider.current?.moveToIdx(idx)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </ContainerBase>
        </section>
    );
}
